// ============================================================================
// CONSTANTS
// ============================================================================

import { useDevice } from "@/components/responsive";
import { useAppSession } from "@/contexts/appSession";
import { useNav } from "@/contexts/navContext";
import { parseTabId, useTabs } from "@/contexts/tabsContext";
import { useResizeObserver } from "@/hooks/useResizeObserver";
import { useSessionState } from "@/hooks/useSessionState";
import { useUser } from "@/hooks/useUser";
import { USER_ACCESS_APP } from "@bloomstack/apis/entityPermissions";
import DockLayout, {
  DividerBox,
  DropDirection,
  LayoutBase,
  TabBase,
  TabData,
} from "rc-dock";
import { FC, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { NavIconButtons, ToolPanelContent } from "../dynamicImports";
import clsx from "clsx";
import { tabRenderers } from "@/chat2/tabRenderers";
import { Chat2Provider } from "@/chat2/Chat2Provider";

/**
 * Tab groups configuration for rc-dock.
 * Groups define behavior and styling for panels.
 */
const DOCK_GROUPS = {
  default: {
    // Allow tabs to be floated (dragged out as floating windows)
    floatable: true,
    // Allow tabs to be moved between panels
    tabLocked: false,
    // Show maximize button in panel header
    maximizable: true,
  },
};

// Tool panel width constants
const TOOL_PANEL_DEFAULT_WIDTH = 300;
const TOOL_PANEL_MIN_WIDTH = 200;
const TOOL_PANEL_MAX_WIDTH = 400;

// ============================================================================
// SHELL COMPONENTS
// ============================================================================

type DesktopShellProps = {
  dock: ReactNode;
  showToolPanel: boolean;
  savedToolPanelWidth: number;
  setToolPanelElement: (el: HTMLDivElement | null) => void;
};

/**
 * Desktop layout shell - DividerBox with optional resizable tool panel
 */
const DesktopShell: FC<DesktopShellProps> = ({
  dock,
  showToolPanel,
  savedToolPanelWidth,
  setToolPanelElement,
}) => {
  // NOTE: We MUST create two dividerbox instances to handle panel toggle.
  // DividerBox does not appear to handle child removals well.
  // Keys help React preserve DockLayout state across the toggle.
  if (showToolPanel) {
    return (
      <DividerBox
        key="main-divider"
        style={{ flex: 1, position: "relative", overflow: "hidden" }}
      >
        <div
          key="tool-panel"
          className="h-full"
          style={{
            width: savedToolPanelWidth,
            minWidth: TOOL_PANEL_MIN_WIDTH,
            maxWidth: TOOL_PANEL_MAX_WIDTH,
            flex: "none",
          }}
        >
          <div
            ref={setToolPanelElement}
            style={{ height: 0, width: "100%" }}
          />
          <ToolPanelContent />
        </div>
        {dock}
      </DividerBox>
    );
  }

  return (
    <DividerBox
      key="main-divider"
      style={{ flex: 1, position: "relative", overflow: "hidden" }}
    >
      {dock}
    </DividerBox>
  );
};

type MobileShellProps = {
  dock: ReactNode;
  showToolPanel: boolean;
  onCloseToolPanel: () => void;
};

/**
 * Mobile layout shell - DividerBox with full-width dock + floating tool panel overlay
 */
const MobileShell: FC<MobileShellProps> = ({
  dock,
  showToolPanel,
  onCloseToolPanel,
}) => {
  return (
    <div className="relative flex-1 flex overflow-hidden">
      {/* DividerBox required by rc-dock */}
      <DividerBox
        key="main-divider-mobile"
        style={{ flex: 1, position: "relative", overflow: "hidden" }}
      >
        {dock}
      </DividerBox>
      
      {/* Overlay OUTSIDE DividerBox so frost can see through dock */}
      <AnimatePresence>
        {showToolPanel && (
          <>
            {/* Backdrop to close panel on tap outside */}
            <motion.div
              key="mobile-backdrop"
              className="absolute inset-0 bg-black/30 z-10"
              onClick={onCloseToolPanel}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            />
            {/* Sliding panel from left */}
            <motion.div
              key="mobile-panel"
              className={clsx(
                "absolute top-0 left-0 h-full z-20",
                "shadow-xl",
                "w-[85vw] max-w-[320px]",
                "border-r border-white"
              )}
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "tween", duration: 0.25, ease: "easeOut" }}
            >
              <ToolPanelContent />
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

type ChatLayoutProps = {
  className?: string;
};

/**
 * ChatLayout2 - Main layout component wrapped with Chat2Provider.
 * This is the public export that App.tsx uses.
 */
export const ChatLayout2: FC<ChatLayoutProps> = ({ className }) => {
  return (
    <Chat2Provider>
      <ChatLayout2Inner className={className} />
    </Chat2Provider>
  );
};

/**
 * ChatLayout2Inner - The actual layout implementation.
 * Must be used inside Chat2Provider.
 */
const ChatLayout2Inner: FC<ChatLayoutProps> = () => {
  const [user, , hasPermission] = useUser();
  const { isInitialized: permissionsInitialized } = useAppSession();
  const { isMobile } = useDevice();
  // Get layout from TabsContext (single source of truth)
  const {
    getLayout,
    setLayout,
    setActivePanel,
    getActivePanelId,
    isMaximized,
    loading: tabsLoading,
  } = useTabs();
  const nav = useNav();
  
  // Get current layout from context
  const layout = getLayout();
  const activePanelId = getActivePanelId();
  
  // Selector for active panel border - targets maxbox when maximized
  const activePanelSelector = isMaximized()
    ? '.dock-box.dock-mbox.dock-mbox-show > .dock-panel'
    : `.dock-panel[data-dockid="${activePanelId}"]`;

  // Whether tool panel should be shown
  const showToolPanel = !!(nav.displayToolPanel && nav.currentPanel?.panel);

  // Tool panel width persistence (desktop only)
  const [savedToolPanelWidth, setSavedToolPanelWidth] = useSessionState<number>(
    "chat2-tool-panel-width",
    TOOL_PANEL_DEFAULT_WIDTH
  );
  const [toolPanelElement, setToolPanelElement] =
    useState<HTMLDivElement | null>(null);
  const toolPanelDimensions = useResizeObserver(toolPanelElement);
  const toolPanelInitializedRef = useRef(false);

  // Save width whenever it changes (debounced, skip first observation) - desktop only
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    if (!isMobile && showToolPanel && toolPanelDimensions?.width) {
      // Skip the first observation - it's just the initial render
      if (!toolPanelInitializedRef.current) {
        toolPanelInitializedRef.current = true;
        return;
      }

      const width = Math.round(toolPanelDimensions.width);
      if (
        width >= TOOL_PANEL_MIN_WIDTH &&
        width <= TOOL_PANEL_MAX_WIDTH &&
        width !== savedToolPanelWidth
      ) {
        // Debounce saves to avoid excessive localStorage writes during drag
        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
        }
        saveTimeoutRef.current = setTimeout(() => {
          setSavedToolPanelWidth(width);
        }, 300);
      }
    }
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [
    isMobile,
    showToolPanel,
    toolPanelDimensions?.width,
    savedToolPanelWidth,
    setSavedToolPanelWidth,
  ]);

  // Click handler to track active panel via closest() on data-dockid
  // Skip when maximized - maxbox panel IDs don't exist in normal layout
  const handleDockClick = useCallback(
    (e: React.MouseEvent) => {
      if (isMaximized()) return;
      
      const panel = (e.target as HTMLElement).closest(
        ".dock-panel[data-dockid]"
      );
      if (panel) {
        const panelId = panel.getAttribute("data-dockid");
        if (panelId && panelId !== activePanelId) {
          setActivePanel(panelId);
        }
      }
    },
    [activePanelId, setActivePanel, isMaximized]
  );

  const handleLayoutChange = useCallback(
    (
      newLayout: LayoutBase,
      currentTabId?: string,
      direction?: DropDirection
    ) => {
      if (tabsLoading) {
        return;
      }

      setLayout(newLayout, currentTabId, direction);
    },
    [tabsLoading, setLayout]
  );

  const handleLoadTab = useCallback((data: TabBase): TabData => {
    const id = data.id;

    if (!id) {
      return {
        id,
        title: "Undefined",
        content: <>Undefined</>,
        closable: true,
        cached: true,
        minWidth: 200,
        minHeight: 200,
      };
    }

    const { type } = parseTabId(id);
    const renderer = tabRenderers[type];

    if (!renderer) {
      console.warn(`No renderer found for tab type "${type}", using fallback`);
      return {
        id,
        title: "Not Implemented",
        content: <>No renderer for type "{type}"</>,
        closable: true,
        cached: true,
        minWidth: 200,
        minHeight: 200,
      };
    }

    const tabData = renderer(id);
    
    // Apply defaults, renderer can override (e.g., Welcome tab sets closable: false)
    return {
      closable: true,
      cached: true,
      minWidth: 200,
      minHeight: 200,
      ...tabData,
    };
  }, []);

  // Create dock element once, pass to appropriate shell
  const dockElement = (
    <DockLayout
      key="dock-layout"
      layout={layout}
      groups={DOCK_GROUPS}
      loadTab={handleLoadTab}
      onLayoutChange={handleLayoutChange}
    />
  );

  const handleCloseToolPanel = useCallback(() => {
    nav.show(false);
  }, [nav]);

  // Permission check - only shown after permissions are initialized (and overlay is gone)
  // NOTE: This must be AFTER all hooks to comply with React's Rules of Hooks
  if (permissionsInitialized && !hasPermission(USER_ACCESS_APP)) {
    return (
      <div className="flex flex-col items-center justify-center h-full w-full text-center bg-zinc-50 dark:bg-zinc-900">
        <div className="text-3xl font-bold">
          Thank you for your interest in Bloomstack!
        </div>
        <div className="text-lg">
          During beta, access to Bloomstack is limited to a select group of
          users.
        </div>
        <div className="text-lg">Please check back later for updates!</div>
        <div className="text-lg">- The Bloomstack Team</div>
        <a href="/" className="text-lg underline mt-4">
          Return to Home
        </a>
      </div>
    );
  }

  return (
    <div
      className={clsx(
        "w-full h-full",
        // Matches ToolPanelContent's desktop surface (zinc-200/zinc-700) so the
        // panel area doesn't flash a different shade while panels load.
        "relative bg-zinc-200 dark:bg-zinc-700 overflow-hidden"
      )}
      onClick={handleDockClick}
    >
      {/* Dynamic style for active panel outline */}
      {activePanelId && (
        <style>{`
            ${activePanelSelector} {
              position: relative;
            }
            ${activePanelSelector}::after {
              content: '';
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 1px;
              border: 1px dotted #f97316;
              pointer-events: none;
              z-index: 999;
            }
          `}</style>
      )}
      <div className="flex h-full w-full overflow-hidden">
        <NavIconButtons key="nav-icons" />
        
        {isMobile ? (
          <MobileShell
            key="mobile-shell"
            dock={dockElement}
            showToolPanel={showToolPanel}
            onCloseToolPanel={handleCloseToolPanel}
          />
        ) : (
          <DesktopShell
            key="desktop-shell"
            dock={dockElement}
            showToolPanel={showToolPanel}
            savedToolPanelWidth={savedToolPanelWidth}
            setToolPanelElement={setToolPanelElement}
          />
        )}
      </div>
      {/* rc-dock styles are in globals.css */}
    </div>
  );
};
